import { useCallback, useEffect, useMemo, useState } from "react";
import { apiService, apiClient, graphBuilderClient } from "../../../services/api";
import { invalidateNodeCache } from "../nodeCache";
import "./Synapses.css";
import NodeForm from "../node/NodeForm";
import SynapseForm from "./SynapseForm";

const TAB_SOURCE = "source";
const TAB_EDGE = "edge";
const TAB_TARGET = "target";

const createRateCodingEntry = (overrides = {}) => ({
  tempId: `rc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  version: "",
  isActive: "true",
  structuredEncodingJson: "",
  naturalLanguageEncoding: "",
  fromTemplateId: "",
  notes: "",
  ...overrides,
});

const mapRateCodingFromRelated = (entries = []) =>
  (entries || []).map((entry, index) =>
    createRateCodingEntry({
      tempId: entry.id || `existing-rc-${index}`,
      id: entry.id || null,
      version:
        entry.version === 0 || entry.version
          ? String(entry.version)
          : "",
      isActive: entry.isActive === false ? "false" : "true",
      structuredEncodingJson: entry.structuredEncoding
        ? JSON.stringify(entry.structuredEncoding, null, 2)
        : "",
      naturalLanguageEncoding: entry.naturalLanguageEncoding || "",
      fromTemplateId: entry.fromTemplateId || "",
      notes: entry.notes || "",
    })
  );

const createNodeSettingEntry = (overrides = {}) => ({
  tempId: `ns-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  neuronTemplateId: "",
  nodeSettingType: "",
  nodeSettingsName: "",
  explanation: "",
  description: "",
  boundedDataJson: "",
  nodeNeuronSettingsJson: "",
  dataClassificationId: "",
  ...overrides,
});

const stringifyJsonField = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    if (!value.trim()) {
      return "";
    }
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
};

const mapNodeSettingsFromRelated = (entries = []) =>
  (entries || []).map((entry, index) =>
    createNodeSettingEntry({
      tempId: entry.id || `existing-ns-${index}`,
      id: entry.id || null,
      neuronTemplateId: entry.neuronTemplateId || "",
      nodeSettingType: entry.type || "",
      nodeSettingsName: entry.name || "",
      explanation: entry.explanation || "",
      description: entry.description || "",
      boundedDataJson: stringifyJsonField(entry.boundedData),
      nodeNeuronSettingsJson: stringifyJsonField(entry.nodeNeuronSettings),
      dataClassificationId: entry.dataClassificationId || "",
    })
  );

const createNodeState = () => ({
  nodeId: null,
  loading: false,
  saving: false,
  error: null,
  success: null,
  form: {
    name: "",
    type: "",
    description: "",
    attributesJson: "",
    fusionMode: "",
    confidenceTiebreak: "",
    fusionParameters: "",
    retentionPolicyId: "",
    nodeKindId: "",
  },
  rateCoding: [],
  settings: [],
});

const createEdgeState = () => ({
  loading: false,
  saving: false,
  error: null,
  success: null,
  data: null,
  form: {
    edgeName: "",
    description: "",
    weight: "",
    delayMs: "",
    attributesJson: "",
    synapseSettingsJson: "",
    synapseKindId: "",
    sourceNodeId: "",
    targetNodeId: "",
  },
  effectiveSettings: {
    resolvedSettingsJson: "",
    source: "",
  },
});


const SynapseEntityModal = ({
  isOpen,
  synapse,
  defaultTab = TAB_SOURCE,
  onClose,
  onEntityUpdated,
  graphId,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [nodeStates, setNodeStates] = useState({
    [TAB_SOURCE]: createNodeState(),
    [TAB_TARGET]: createNodeState(),
  });
  const [edgeState, setEdgeState] = useState(createEdgeState());

  const synapseId = synapse?.synapseid || synapse?.SynapseId || synapse?.id;
  const sourceNodeId =
    synapse?.sourcenodeid || synapse?.SourceNodeId || synapse?.sourceNodeId;
  const targetNodeId =
    synapse?.targetnodeid || synapse?.TargetNodeId || synapse?.targetNodeId;

  const tabConfig = useMemo(
    () => {
      const isAddMode = !synapse;
      if (isAddMode) {
        // In add mode, only show Edge tab
        return [
          { id: TAB_EDGE, label: "Edge", enabled: true },
        ];
      }
      return [
        { id: TAB_SOURCE, label: "Source", enabled: Boolean(sourceNodeId) },
        { id: TAB_EDGE, label: "Edge", enabled: Boolean(synapseId) },
        { id: TAB_TARGET, label: "Target", enabled: Boolean(targetNodeId) },
      ];
    },
    [sourceNodeId, targetNodeId, synapseId, synapse]
  );

  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  useEffect(() => {
    if (!isOpen) {
      setNodeStates({
        [TAB_SOURCE]: createNodeState(),
        [TAB_TARGET]: createNodeState(),
      });
      setEdgeState(createEdgeState());
    } else if (isOpen && !synapse) {
      setEdgeState(createEdgeState());
    }
  }, [isOpen, synapse]);

  // Helper functions for attributes (JSON format) - used for both nodes and synapses
  const attributesToJson = (attributes) => {
    if (!attributes) {
      return JSON.stringify({ attributes: [] }, null, 2);
    }
    if (Array.isArray(attributes)) {
      return JSON.stringify({ attributes }, null, 2);
    }
    if (typeof attributes === "object") {
      return JSON.stringify(attributes, null, 2);
    }
    return JSON.stringify({ attributes: [attributes] }, null, 2);
  };

  const parseAttributesFromJson = (jsonText) => {
    if (!jsonText || !jsonText.trim()) {
      return null;
    }
    try {
      const parsed = JSON.parse(jsonText);
      // Backend expects either an array or an object with 'attributes' key
      if (Array.isArray(parsed)) {
        return parsed;
      } else if (parsed.attributes && Array.isArray(parsed.attributes)) {
        return parsed.attributes;
      } else {
        // If it's an object, keep it as is for full JSON support
        return parsed;
      }
    } catch (error) {
      throw new Error(`Invalid JSON in attributes: ${error.message}`);
    }
  };

  // Helper functions for synapse settings (JSON format)
  const settingsToJson = (settings) => {
    if (!settings) {
      return JSON.stringify({}, null, 2);
    }
    if (typeof settings === "object") {
      return JSON.stringify(settings, null, 2);
    }
    if (typeof settings === "string") {
      try {
        const parsed = JSON.parse(settings);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return JSON.stringify({}, null, 2);
      }
    }
    return JSON.stringify({}, null, 2);
  };

  // Helper function for effective settings JSON
  const effectiveSettingsToJson = (settings) => {
    if (!settings) {
      return "";
    }
    if (typeof settings === "object") {
      return JSON.stringify(settings, null, 2);
    }
    if (typeof settings === "string") {
      try {
        const parsed = JSON.parse(settings);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return settings;
      }
    }
    return "";
  };

  const parseSettingsFromJson = (jsonText) => {
    if (!jsonText || !jsonText.trim()) {
      return null;
    }
    try {
      const parsed = JSON.parse(jsonText);
      if (typeof parsed === "object") {
        return parsed;
      }
      return null;
    } catch (error) {
      throw new Error(`Invalid JSON in synapse settings: ${error.message}`);
    }
  };

  const loadNodeDetails = useCallback(
    async (kind, nodeId) => {
      if (!nodeId || !isOpen) {
        return;
      }
      setNodeStates((prev) => ({
        ...prev,
        [kind]: {
          ...createNodeState(),
          ...prev[kind],
          nodeId,
          loading: true,
          error: null,
          success: null,
        },
      }));
      try {
        const response = await apiService.getNode(nodeId, {
          include_related: true,
        });
        if (response.success && response.node) {
          const node = response.node;
          const related = node.related || {};
          const fusionPolicy = related.fusionPolicy || {};
          setNodeStates((prev) => ({
            ...prev,
            [kind]: {
              ...prev[kind],
              node,
              nodeId,
              loading: false,
              form: {
                name: node.name || "",
                type: node.type || "",
                description: node.description || "",
                attributesJson: attributesToJson(node.attributes),
                fusionMode: fusionPolicy.fusionMode || "",
                confidenceTiebreak: fusionPolicy.confidenceTiebreak || "",
                fusionParameters: fusionPolicy.parameters
                  ? JSON.stringify(fusionPolicy.parameters, null, 2)
                  : "",
                retentionPolicyId: node.retentionPolicyId || "",
                nodeKindId: node.nodeKindId || "",
              },
              rateCoding: mapRateCodingFromRelated(related.rateCoding),
              settings: mapNodeSettingsFromRelated(related.settings),
            },
          }));
        } else {
          throw new Error(response.message || "Unable to load node details.");
        }
      } catch (error) {
        setNodeStates((prev) => ({
          ...prev,
          [kind]: {
            ...prev[kind],
            loading: false,
            error: error.message || "Failed to load node.",
          },
        }));
      }
    },
    [isOpen]
  );

  const loadEdgeDetails = useCallback(async () => {
    if (!synapseId || !isOpen) {
      return;
    }
    setEdgeState((prev) => ({
      ...createEdgeState(),
      ...prev,
      loading: true,
      error: null,
      success: null,
    }));
    try {
      const response = await apiService.getSynapse(synapseId);
      if (response.success && response.synapse) {
        const detail = response.synapse;
        const effectiveSettings = detail.effectiveSettings || {};
        setEdgeState({
          loading: false,
          saving: false,
          error: null,
          success: null,
          data: detail,
          form: {
            edgeName: detail.edgeName || synapse?.edgename || "Edge",
            description: detail.description || "",
            weight: detail.weight ?? "",
            delayMs: detail.delayMs ?? "",
            attributesJson: attributesToJson(detail.attributes),
            synapseSettingsJson: settingsToJson(detail.synapseSettings),
            synapseKindId: detail.synapseKindId || "",
          },
          effectiveSettings: {
            resolvedSettingsJson: effectiveSettingsToJson(effectiveSettings.resolvedSettings),
            source: effectiveSettings.source || "",
          },
        });
      } else {
        throw new Error(response.message || "Unable to load synapse.");
      }
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to load synapse.",
      }));
    }
  }, [synapseId, synapse?.edgename, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Only load details if we have a synapse (not in add mode)
    if (synapse) {
      if (sourceNodeId) {
        loadNodeDetails(TAB_SOURCE, sourceNodeId);
      }
      if (targetNodeId) {
        loadNodeDetails(TAB_TARGET, targetNodeId);
      }
      loadEdgeDetails();
    }
  }, [isOpen, loadNodeDetails, loadEdgeDetails, sourceNodeId, targetNodeId, synapse]);

  const handleNodeInputChange = (kind, event) => {
    const { name, value } = event.target;
    setNodeStates((prev) => ({
      ...prev,
      [kind]: {
        ...prev[kind],
        form: {
          ...prev[kind].form,
          [name]: value,
        },
      },
    }));
  };

  const handleSaveNode = async (kind) => {
    const nodeId = kind === TAB_SOURCE ? sourceNodeId : targetNodeId;
    if (!nodeId) {
      return;
    }
    const currentState = nodeStates[kind];
    setNodeStates((prev) => ({
      ...prev,
      [kind]: {
        ...prev[kind],
        saving: true,
        error: null,
        success: null,
      },
    }));

    // Parse attributes JSON
    let attributes = null;
    try {
      attributes = parseAttributesFromJson(currentState.form.attributesJson);
    } catch (error) {
      setNodeStates((prev) => ({
        ...prev,
        [kind]: {
          ...prev[kind],
          saving: false,
          error: error.message || "Invalid JSON in attributes.",
        },
      }));
      return;
    }

    const payload = {
      name: currentState.form.name.trim(),
      type: currentState.form.type.trim(),
      description: currentState.form.description.trim(),
    };

    // Only include attributes if they were provided
    if (attributes !== null) {
      payload.attributes = attributes;
    }

    // Add retentionPolicyId if provided
    if (
      currentState.form.retentionPolicyId !== undefined &&
      currentState.form.retentionPolicyId !== ""
    ) {
      payload.retentionPolicyId =
        currentState.form.retentionPolicyId.trim() || null;
    }

    // Add nodeKindId if provided
    if (
      currentState.form.nodeKindId !== undefined &&
      currentState.form.nodeKindId !== ""
    ) {
      payload.nodeKindId = currentState.form.nodeKindId.trim() || null;
    }

    // Add related entities if they have values
    const related = {};
    if (
      currentState.form.fusionMode ||
      currentState.form.confidenceTiebreak ||
      currentState.form.fusionParameters
    ) {
      related.fusionPolicy = {
        fusionMode: currentState.form.fusionMode.trim() || undefined,
        confidenceTiebreak:
          currentState.form.confidenceTiebreak.trim() || undefined,
        parameters: currentState.form.fusionParameters
          ? (() => {
              try {
                return JSON.parse(currentState.form.fusionParameters);
              } catch {
                return {};
              }
            })()
          : undefined,
      };
    }

    // Handle rate coding entries (many-to-one)
    const rateCodingEntries = currentState.rateCoding || [];
    const rateCodingPayload = [];
    for (let index = 0; index < rateCodingEntries.length; index += 1) {
      const entry = rateCodingEntries[index];
      const versionValue = entry.version?.toString().trim();
      const hasContent =
        (versionValue && versionValue.length > 0) ||
        (entry.structuredEncodingJson &&
          entry.structuredEncodingJson.trim().length > 0) ||
        (entry.naturalLanguageEncoding &&
          entry.naturalLanguageEncoding.trim().length > 0) ||
        (entry.fromTemplateId &&
          entry.fromTemplateId.trim().length > 0) ||
        (entry.notes && entry.notes.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      let structuredEncoding;
      if (entry.structuredEncodingJson && entry.structuredEncodingJson.trim()) {
        try {
          structuredEncoding = JSON.parse(entry.structuredEncodingJson);
        } catch (error) {
          setNodeStates((prev) => ({
            ...prev,
            [kind]: {
              ...prev[kind],
              saving: false,
              error:
                `Relevance scoring ${index + 1}: Invalid structured mapping JSON. ${
                  error.message || ""
                }`.trim(),
            },
          }));
          return;
        }
      }

      let version = undefined;
      if (versionValue && versionValue.length > 0) {
        const parsedVersion = Number(versionValue);
        if (Number.isNaN(parsedVersion)) {
          setNodeStates((prev) => ({
            ...prev,
            [kind]: {
              ...prev[kind],
              saving: false,
              error: `Relevance scoring ${index + 1}: Version must be a number.`,
            },
          }));
          return;
        }
        version = parsedVersion;
      }

      rateCodingPayload.push({
        id: entry.id || undefined,
        version,
        isActive: entry.isActive === "false" ? false : true,
        structuredEncoding: structuredEncoding ?? undefined,
        naturalLanguageEncoding:
          entry.naturalLanguageEncoding?.trim() || undefined,
        fromTemplateId: entry.fromTemplateId?.trim() || undefined,
        notes: entry.notes?.trim() || undefined,
      });
    }

    if (rateCodingPayload.length > 0) {
      related.rateCoding = rateCodingPayload;
    }

    // Handle node settings entries (many-to-one)
    const nodeSettingsEntries = currentState.settings || [];
    const nodeSettingsPayload = [];
    for (let index = 0; index < nodeSettingsEntries.length; index += 1) {
      const entry = nodeSettingsEntries[index];
      const hasContent =
        (entry.neuronTemplateId && entry.neuronTemplateId.trim().length > 0) ||
        (entry.nodeSettingType && entry.nodeSettingType.trim().length > 0) ||
        (entry.nodeSettingsName && entry.nodeSettingsName.trim().length > 0) ||
        (entry.explanation && entry.explanation.trim().length > 0) ||
        (entry.description && entry.description.trim().length > 0) ||
        (entry.boundedDataJson && entry.boundedDataJson.trim().length > 0) ||
        (entry.nodeNeuronSettingsJson &&
          entry.nodeNeuronSettingsJson.trim().length > 0) ||
        (entry.dataClassificationId &&
          entry.dataClassificationId.trim().length > 0);

      if (!hasContent) {
        continue;
      }

      let boundedData;
      if (entry.boundedDataJson && entry.boundedDataJson.trim()) {
        try {
          boundedData = JSON.parse(entry.boundedDataJson);
        } catch (error) {
          setNodeStates((prev) => ({
            ...prev,
            [kind]: {
              ...prev[kind],
              saving: false,
              error: `Node setting ${index + 1}: Invalid bounded data JSON. ${
                error.message || ""
              }`.trim(),
            },
          }));
          return;
        }
      }

      let nodeNeuronSettings;
      if (entry.nodeNeuronSettingsJson && entry.nodeNeuronSettingsJson.trim()) {
        try {
          nodeNeuronSettings = JSON.parse(entry.nodeNeuronSettingsJson);
        } catch (error) {
          setNodeStates((prev) => ({
            ...prev,
            [kind]: {
              ...prev[kind],
              saving: false,
              error: `Node setting ${
                index + 1
              }: Invalid node settings JSON. ${error.message || ""}`.trim(),
            },
          }));
          return;
        }
      }

      nodeSettingsPayload.push({
        id: entry.id || undefined,
        neuronTemplateId: entry.neuronTemplateId?.trim() || undefined,
        type: entry.nodeSettingType?.trim() || undefined,
        name: entry.nodeSettingsName?.trim() || undefined,
        explanation: entry.explanation?.trim() || undefined,
        description: entry.description?.trim() || undefined,
        boundedData: boundedData ?? undefined,
        nodeNeuronSettings: nodeNeuronSettings ?? undefined,
        dataClassificationId: entry.dataClassificationId?.trim() || undefined,
      });
    }

    if (nodeSettingsPayload.length > 0) {
      related.settings = nodeSettingsPayload;
    }

    if (Object.keys(related).length > 0) {
      payload.related = related;
    }

    try {
      const response = await apiService.updateNode(nodeId, payload);
      if (response.success && response.node) {
        const refreshedRateCoding = response.node?.related?.rateCoding
          ? mapRateCodingFromRelated(response.node.related.rateCoding)
          : currentState.rateCoding;
        const refreshedSettings = response.node?.related?.settings
          ? mapNodeSettingsFromRelated(response.node.related.settings)
          : currentState.settings;
        setNodeStates((prev) => ({
          ...prev,
          [kind]: {
            ...prev[kind],
            saving: false,
            success: "Changes saved",
            node: response.node,
            rateCoding: refreshedRateCoding,
            settings: refreshedSettings,
          },
        }));
        // Invalidate node cache when node is updated
        if (graphId) {
          invalidateNodeCache(graphId);
        }
        if (typeof onEntityUpdated === "function") {
          onEntityUpdated(kind);
        }
      } else {
        throw new Error(response.message || "Unable to update node.");
      }
    } catch (error) {
      setNodeStates((prev) => ({
        ...prev,
        [kind]: {
          ...prev[kind],
          saving: false,
          error: error.message || "Failed to update node.",
        },
      }));
    }
  };

  const handleRateCodingChange = (kind, index, field, value) => {
    setNodeStates((prev) => {
      const currentEntries = prev[kind].rateCoding || [];
      const nextEntries = currentEntries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      return {
        ...prev,
        [kind]: {
          ...prev[kind],
          rateCoding: nextEntries,
        },
      };
    });
  };

  const handleAddRateCoding = (kind) => {
    setNodeStates((prev) => ({
      ...prev,
      [kind]: {
        ...prev[kind],
        rateCoding: [...(prev[kind].rateCoding || []), createRateCodingEntry()],
      },
    }));
  };

  const handleRemoveRateCoding = (kind, index) => {
    setNodeStates((prev) => {
      const currentEntries = prev[kind].rateCoding || [];
      return {
        ...prev,
        [kind]: {
          ...prev[kind],
          rateCoding: currentEntries.filter((_, i) => i !== index),
        },
      };
    });
  };

  const handleNodeSettingsChange = (kind, index, field, value) => {
    setNodeStates((prev) => {
      const currentEntries = prev[kind].settings || [];
      const nextEntries = currentEntries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      );
      return {
        ...prev,
        [kind]: {
          ...prev[kind],
          settings: nextEntries,
        },
      };
    });
  };

  const handleAddNodeSetting = (kind) => {
    setNodeStates((prev) => ({
      ...prev,
      [kind]: {
        ...prev[kind],
        settings: [...(prev[kind].settings || []), createNodeSettingEntry()],
      },
    }));
  };

  const handleRemoveNodeSetting = (kind, index) => {
    setNodeStates((prev) => {
      const currentEntries = prev[kind].settings || [];
      return {
        ...prev,
        [kind]: {
          ...prev[kind],
          settings: currentEntries.filter((_, i) => i !== index),
        },
      };
    });
  };

  const handleEdgeInputChange = (event) => {
    const { name, value } = event.target;
    setEdgeState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
    }));
  };

  const handleEffectiveSettingsChange = (field, value) => {
    setEdgeState((prev) => ({
      ...prev,
      effectiveSettings: {
        ...prev.effectiveSettings,
        [field]: value,
      },
    }));
  };

  const handleCreateSynapse = async () => {
    if (!graphId) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Graph ID is required to create a synapse.",
      }));
      return;
    }

    const sourceNodeId = edgeState.form.sourceNodeId?.trim();
    const targetNodeId = edgeState.form.targetNodeId?.trim();

    if (!sourceNodeId) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Source node ID is required.",
      }));
      return;
    }

    if (!targetNodeId) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Target node ID is required.",
      }));
      return;
    }

    const normalizedWeight =
      edgeState.form.weight === "" ? null : Number(edgeState.form.weight);
    if (edgeState.form.weight !== "" && Number.isNaN(normalizedWeight)) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Weight must be a number.",
      }));
      return;
    }

    const normalizedDelay =
      edgeState.form.delayMs === "" ? null : Number(edgeState.form.delayMs);
    if (edgeState.form.delayMs !== "" && Number.isNaN(normalizedDelay)) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Delay must be a number.",
      }));
      return;
    }

    // Parse attributes JSON
    let attributes = null;
    try {
      attributes = parseAttributesFromJson(edgeState.form.attributesJson);
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        error: error.message || "Invalid JSON in attributes.",
      }));
      return;
    }

    // Parse synapse settings JSON
    let synapseSettings = null;
    try {
      synapseSettings = parseSettingsFromJson(
        edgeState.form.synapseSettingsJson
      );
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        error: error.message || "Invalid JSON in synapse settings.",
      }));
      return;
    }

    setEdgeState((prev) => ({
      ...prev,
      saving: true,
      error: null,
      success: null,
    }));

    const payload = {
      graphId,
      sourceNodeId,
      targetNodeId,
      description: edgeState.form.description.trim() || undefined,
      weight: normalizedWeight,
      delayMs: normalizedDelay,
    };

    // Only include edgeName if provided
    if (edgeState.form.edgeName?.trim()) {
      payload.edgeName = edgeState.form.edgeName.trim();
    }

    // Include synapseKindId if selected
    if (edgeState.form.synapseKindId) {
      payload.synapseKindId = edgeState.form.synapseKindId;
    }

    // Only include attributes if they were provided
    if (attributes !== null) {
      payload.attributes = attributes;
    }

    // Only include synapseSettings if they were provided
    if (synapseSettings !== null) {
      payload.synapseSettings = synapseSettings;
    }

    try {
      const response = await apiService.createSynapse(payload);
      if (response.success) {
        setEdgeState((prev) => ({
          ...prev,
          saving: false,
          success: "Connection created successfully",
        }));
        // Invalidate node cache when synapse is created (graph changed)
        if (graphId) {
          invalidateNodeCache(graphId);
        }
        if (typeof onEntityUpdated === "function") {
          onEntityUpdated(TAB_EDGE);
        }
        // Close modal after a short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(response.message || "Unable to create synapse.");
      }
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || "Failed to create synapse.",
      }));
    }
  };

  const handleSaveEdge = async () => {
    // In add mode, call handleCreateSynapse instead
    if (!synapseId) {
      await handleCreateSynapse();
      return;
    }

    const normalizedWeight =
      edgeState.form.weight === "" ? null : Number(edgeState.form.weight);
    if (Number.isNaN(normalizedWeight)) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Weight must be a number.",
      }));
      return;
    }

    const normalizedDelay =
      edgeState.form.delayMs === "" ? null : Number(edgeState.form.delayMs);
    if (Number.isNaN(normalizedDelay)) {
      setEdgeState((prev) => ({
        ...prev,
        error: "Delay must be a number.",
      }));
      return;
    }

    // Parse attributes JSON
    let attributes = null;
    try {
      attributes = parseAttributesFromJson(edgeState.form.attributesJson);
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        error: error.message || "Invalid JSON in attributes.",
      }));
      return;
    }

    // Parse synapse settings JSON
    let synapseSettings = null;
    try {
      synapseSettings = parseSettingsFromJson(
        edgeState.form.synapseSettingsJson
      );
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        error: error.message || "Invalid JSON in synapse settings.",
      }));
      return;
    }

    // Parse effective settings resolvedSettings JSON
    let resolvedSettings = null;
    try {
      const resolvedSettingsJson = edgeState.effectiveSettings?.resolvedSettingsJson;
      if (resolvedSettingsJson && resolvedSettingsJson.trim()) {
        resolvedSettings = JSON.parse(resolvedSettingsJson);
        if (typeof resolvedSettings !== "object") {
          throw new Error("Resolved settings must be a JSON object.");
        }
      }
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        error: error.message || "Invalid JSON in resolved settings.",
      }));
      return;
    }

    setEdgeState((prev) => ({
      ...prev,
      saving: true,
      error: null,
      success: null,
    }));

    const payload = {
      description: edgeState.form.description.trim(),
      weight: normalizedWeight,
      delayMs: normalizedDelay,
    };

    // Only include attributes if they were provided
    if (attributes !== null) {
      payload.attributes = attributes;
    }

    // Only include synapseSettings if they were provided
    if (synapseSettings !== null) {
      payload.synapseSettings = synapseSettings;
    }

    // Handle effective settings (one-to-one)
    const effectiveSettings = edgeState.effectiveSettings || {};
    const hasEffectiveSettings =
      (resolvedSettings !== null) ||
      (effectiveSettings.source && effectiveSettings.source.trim().length > 0);
    
    if (hasEffectiveSettings) {
      payload.effectiveSettings = {};
      if (resolvedSettings !== null) {
        payload.effectiveSettings.resolvedSettings = resolvedSettings;
      }
      if (effectiveSettings.source && effectiveSettings.source.trim().length > 0) {
        payload.effectiveSettings.source = effectiveSettings.source.trim();
      }
    }

    try {
      const response = await apiService.updateSynapse(synapseId, payload);
      if (response.success && response.synapse) {
        setEdgeState((prev) => ({
          ...prev,
          saving: false,
          success: "Changes saved",
          data: response.synapse,
        }));
        // Invalidate node cache when synapse is updated (graph changed)
        if (graphId) {
          invalidateNodeCache(graphId);
          try {
            await graphBuilderClient.post('/api/runtime/on-graph-save', { graph_id: graphId });
          } catch (e) {
            console.warn('[SynapseEntityModal] on-graph-save trigger failed:', e);
          }
        }
        if (typeof onEntityUpdated === "function") {
          onEntityUpdated(TAB_EDGE);
        }
      } else {
        throw new Error(response.message || "Unable to update synapse.");
      }
    } catch (error) {
      setEdgeState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || "Failed to update synapse.",
      }));
    }
  };

  const renderNodeForm = (kind) => {
    const state = nodeStates[kind];
    const nodeId = kind === TAB_SOURCE ? sourceNodeId : targetNodeId;

    if (!nodeId) {
      return <p className="synapse-entity-modal__empty">No node connected.</p>;
    }

    if (state.loading) {
      return (
        <p className="synapse-entity-modal__status">Loading node details…</p>
      );
    }

    return (
      <NodeForm
        kind={kind}
        state={state}
        handleNodeInputChange={handleNodeInputChange}
        handleSaveNode={handleSaveNode}
        handleRateCodingChange={handleRateCodingChange}
        handleAddRateCoding={handleAddRateCoding}
        handleRemoveRateCoding={handleRemoveRateCoding}
        handleNodeSettingsChange={handleNodeSettingsChange}
        handleAddNodeSetting={handleAddNodeSetting}
        handleRemoveNodeSetting={handleRemoveNodeSetting}
      />
    );
  };

  const renderEdgeForm = () => {
    const isAddMode = !synapse;
    
    if (!synapseId) {
      return (
        <SynapseForm
          edgeState={{
            ...edgeState,
            handleEffectiveSettingsChange: handleEffectiveSettingsChange,
          }}
          handleEdgeInputChange={handleEdgeInputChange}
          handleSaveEdge={handleSaveEdge}
          isAddMode={isAddMode}
          graphId={graphId}
        />
      );
    }

    if (edgeState.loading) {
      return (
        <p className="synapse-entity-modal__status">Loading edge details…</p>
      );
    }

    return (
      <SynapseForm
        edgeState={{
          ...edgeState,
          handleEffectiveSettingsChange: handleEffectiveSettingsChange,
        }}
        handleEdgeInputChange={handleEdgeInputChange}
        handleSaveEdge={handleSaveEdge}
        isAddMode={false}
        graphId={graphId}
      />
    );
  };

  // Allow modal to open even without synapse (for add mode)
  if (!isOpen) {
    return null;
  }

  const isAddMode = !synapse;

  return (
    <div className="synapse-modal-overlay" onClick={onClose}>
      <div
        className="synapse-entity-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="synapse-entity-modal__header">
          <div>
            <p className="synapse-entity-modal__eyebrow">Connection</p>
            <h2>{isAddMode ? "Create connection" : "Edit connection"}</h2>
            {!isAddMode && (
              <p className="synapse-entity-modal__subhead">
                {synapse?.sourcenodename || synapse?.sourceNodeName || "—"} →{" "}
                {synapse?.targetnodename || synapse?.targetNodeName || "—"}
              </p>
            )}
          </div>
          <button
            type="button"
            className="synapse-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="synapse-entity-modal__tabs">
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`synapse-entity-modal__tab ${
                activeTab === tab.id ? "is-active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
              disabled={!tab.enabled}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="synapse-entity-modal__body">
          {activeTab === TAB_SOURCE && renderNodeForm(TAB_SOURCE)}
          {activeTab === TAB_EDGE && renderEdgeForm()}
          {activeTab === TAB_TARGET && renderNodeForm(TAB_TARGET)}
        </div>
      </div>
    </div>
  );
};

export default SynapseEntityModal;
